import React, { FC, useCallback, useMemo, useState } from 'react';
import { Modal } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AggregatedProvider } from '@workboard/action-item';
import { useMirrorAiForm, MirrorItem } from './useMirrorAiForm';
import {
  SimpleArrayField,
  ArrayFieldDef,
  InlineAutocompleteField,
  InlineSelectField,
} from '../form';
import { WorkstreamAutocompleteField } from '../workstream/autocompleteField';
import { WorkstreamColumnField } from '../ActionItemPopup/WorkstreamColumn';
import { Loading } from '../Loading';
import * as testIds from './testIds';
import { getGlobalConfig } from '../global/ConfigProvider';
import { onMessage } from '../global/onMessage';

const Wrapper = styled.div`
  width: 500px;
  min-height: 400px;
`;
const ColumnWrapper = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
`;

interface IProps {
  aiId: string;
  initialValue?: Date;
  onClose?: () => void;
  title?: string;
}

export const MirrorAiModal: FC<IProps> = ({
  aiId,
  onClose = () => undefined,
  title,
}) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(true);
  const {
    formErrors,
    getFieldProps,
    isProcessing,
    loading,
    submitForm,
    validateMirrorItem,
  } = useMirrorAiForm(aiId);

  const closeCallback = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const submitCallback = useCallback(async () => {
    try {
      await submitForm();
      setOpen(false);
      setTimeout(() => onClose(), 5000);
    } catch (e) {
      console.error(e);
    }
  }, [onClose, submitForm]);

  const fields = useMemo<ArrayFieldDef<any, Partial<MirrorItem>>[]>(
    () => [
      {
        name: 'workstream',
        label: t('Workstream'),
        value: '',
        render: ({ value, ...props }) => (
          <InlineAutocompleteField
            data-testid={testIds.mirroringWorkstreamField}
          >
            <WorkstreamAutocompleteField
              {...props}
              label=""
              aiId={aiId}
              filter={(ws) => {
                const BOARD_TYPE_CUSTOM = '2';
                return ws.board_type === BOARD_TYPE_CUSTOM;
              }}
              value={value?.id}
            />
          </InlineAutocompleteField>
        ),
        renderValue: ({ value }) => value.workstream?.label,
      },
      {
        name: 'column',
        label: t('Column'),
        value: '',
        render: ({ context, isDisabled, ...props }) => (
          <InlineSelectField data-testid={testIds.mirroringColumnField}>
            <ColumnWrapper>
              <WorkstreamColumnField
                {...props}
                isDisabled={isDisabled || !context.workstream?.id}
                workstreamId={context.workstream?.id}
                value={props.value?.id}
                label=""
              />
            </ColumnWrapper>
          </InlineSelectField>
        ),
        renderValue: ({ value }) => value.column?.label,
      },
    ],
    [aiId, t]
  );
  const titleText = (title ?? 'action').toLowerCase();
  const modalTitle = `${t("Add {{titleText}} to multiple boards", { titleText })}`;

  return (
    <AggregatedProvider
      id={aiId}
      config={getGlobalConfig()}
      onMessage={onMessage}
    >
      <Modal
        disableSubmit={Object.values(formErrors).length || isProcessing}
        removeSubmit={false}
        dismissLabel={t('Cancel')}
        isOpen={isOpen}
        onDismiss={closeCallback}
        onSubmit={submitCallback}
        required={false}
        submitLabel={t('Save')}
        title={modalTitle}
      >
        <Wrapper data-testid={testIds.mirroringArrayField}>
          {loading ? (
            <Loading />
          ) : (
            <SimpleArrayField<MirrorItem>
              {...getFieldProps('items')}
              errors={formErrors?.items as any}
              fields={fields}
              validateRecord={validateMirrorItem}
            />
          )}
        </Wrapper>
      </Modal>
    </AggregatedProvider>
  );
};
